/* Built In Imports */
import NextLink from 'next/link';
import { useState } from 'react';

/* External Imports */
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Button, Icon, Image, Link, Text } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { useMediaQuery } from 'react-responsive';
import Slider from 'react-slick';

/* Internal Imports */
/* Components */
import StructuredTextParser from '@moms/Utility/StructuredTextParser';
import { isOpenInNewTab, refineUrlDomain } from '@moms/Utility/utils';
import InnerSt from '@moms/styleData/InnerSt';
import MiracleInActionSt from '@moms/styleData/MiracleInActionSt';
import { nanoid } from 'nanoid';

/* Styles */
/* Services */

/**
 *
 * @param {Object} sectionContent
 * @param {String} region
 * @param {String} lang
 * @returns
 */

const SliderMain = ({ region, lang, sectionContent }) => {
  const CardData =
    sectionContent.find(
      item => item._modelApiKey === 'testimonials_group_v2'
    ) || sectionContent?.[0];

  const isArrow = useMediaQuery({ maxWidth: 767 });
  const isTwoSlide = useMediaQuery({ maxWidth: 1023 });
  const totalDevideSlide = isTwoSlide ? 1 : 2;
  const [currentSlide, setCurrentSlide] = useState(0);

  const totalSlides = Math.ceil(
    (CardData?.testimonials?.length || 0) / totalDevideSlide
  );
  const CustomPrevArrow = ({ onClick }) => (
    <Box
      left={{ md: '-50px', lg: '-60px' }}
      {...MiracleInActionSt[lang]?.sliderIcon}
      // cursor={currentSlide === 0 ? 'not-allowed' : 'pointer'}
      // opacity={currentSlide === 0 ? 0.5 : 1}
      // onClick={currentSlide === 0 ? undefined : onClick}
      cursor="pointer"
      onClick={onClick}
    >
      <Icon as={ChevronLeftIcon} boxSize={12} color="#fff" />
    </Box>
  );

  const CustomNextArrow = ({ onClick }) => (
    <Box
      right={{ md: '-50px', lg: '-60px' }}
      {...MiracleInActionSt[lang]?.sliderIcon}
      // cursor={currentSlide === totalSlides - 1 ? 'not-allowed' : 'pointer'}
      // opacity={currentSlide === totalSlides - 1 ? 0.5 : 1}
      // onClick={currentSlide === totalSlides - 1 ? undefined : onClick}
      cursor="pointer"
      onClick={onClick}
    >
      <Icon as={ChevronRightIcon} boxSize={12} color="#fff" />
    </Box>
  );

  const settings = {
    dots: true,
    speed: 1500,
    slidesToShow: 2,
    slidesToScroll: 2,
    arrows: !isArrow ? true : false,
    infinite: true,
    cssEase: 'ease-in-out',
    autoplay: true,
    autoplaySpeed: 5000,
    beforeChange: (oldIndex, newIndex) =>
      setCurrentSlide(newIndex / totalDevideSlide),
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    customPaging: i => (
      <Box
        width={{ base: '11.39px' }}
        height={{ base: '11.39px' }}
        borderRadius="50%"
        mt={{ base: '1rem' }}
        bgColor=" #FFFFFF33"
        transition="background-color 0.3s, opacity 0.3s"
        css={{
          '.slick-active &': {
            backgroundColor: '#EC1C2D',
            opacity: '1',
          },
        }}
        _hover={{ bgColor: '#08152D' }}
      />
    ),
    responsive: [
      {
        breakpoint: 320,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <Slider {...settings}>
      {CardData?.testimonials?.map((testimonial, index) => {
        return (
          <Box
            key={testimonial.id || nanoid()}
            display="flex !important"
            alignItems="center"
            borderRight={{
              lg: index % 2 === 0 ? '1px solid #ffffff33' : 'none',
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              px={{ base: '23px', md: '25px', xl: '30px' }}
              width="100%"
            >
              <Box
                display="flex"
                alignItems="flex-end"
                gap={{ base: '12px', md: '20px', lg: '30px' }}
              >
                <Image
                  src={testimonial?.photo?.url}
                  alt={testimonial?.photo?.alt}
                  title={testimonial?.photo?.title}
                  w={{ base: '70px', md: '106px' }}
                  h={{ base: '70px', md: '106px' }}
                  objectFit="cover"
                />
                <Box pb={{ base: '0', md: '10px', lg: '20px' }}>
                  <StructuredTextParser
                    {...MiracleInActionSt[lang]?.userNameText}
                    str={testimonial?.userName}
                    mt="0"
                    pb={{ md: '4px' }}
                  />
                  <StructuredTextParser
                    {...MiracleInActionSt[lang]?.testimonialTitle}
                    str={render(testimonial?.testimonialTitle)}
                    mt="0"
                  />
                </Box>
              </Box>
              <Box pt={{ base: '16px', md: '20px' }}>
                <StructuredTextParser
                  {...MiracleInActionSt[lang]?.testimonialText}
                  str={render(testimonial?.testimonialText)}
                  mt="0"
                />
                <StructuredTextParser
                  {...MiracleInActionSt[lang]?.details}
                  str={render(testimonial?.details)}
                  mt="0"
                  pt={{ base: '16px', md: '40px' }}
                  pb={{ base: '32px', md: '24px' }}
                />
              </Box>
              <style jsx global>{`
                .slick-dots li button:before {
                  color: rgba(255, 255, 255, 0.2) !important;
                  font-size: 12px !important;
                }
                .slick-dots li.slick-active button:before {
                  color: #ec1c2d !important;
                }
                .slick-slider .slick-dots {
                  bottom: -48px !important;
                  text-align: center;
                }
                .slick-dots li {
                  margin: 0 4px !important;
                }
                slick-slide:focus {
                  outline: none !important;
                  border: none !important;
                }
                .slick-slide div:focus {
                  outline: none !important;
                }
                .slick-slide img {
                  outline: none !important;
                }
              `}</style>
            </Box>
          </Box>
        );
      })}
    </Slider>
  );
};

const MainHeading = ({ region, lang, sectionContent }) => {
  const CardData =
    sectionContent.find(
      item => item._modelApiKey === 'testimonials_group_v2'
    ) || sectionContent?.[0];

  return (
    <Box mx="auto" textAlign="center" pb={{ base: '48px', md: '64px' }}>
      <Box
        {...MiracleInActionSt[lang]?.titleText}
        display="flex"
        justifyContent="center"
        gap={{ base: '5px', sm: '12px' }}
        flexDir={{ base: 'column' }}
      >
        <Text fontStyle="italic">{CardData?.title[0]?.titleText}</Text>
        <Text>{CardData?.title[0]?.titleSubtext}</Text>
      </Box>
    </Box>
  );
};

const LowerButton = ({ region, lang, sectionContent }) => {
  const buttonData =
    sectionContent.find(item => item._modelApiKey === 'simple_button') ||
    sectionContent?.[1];

  return (
    <Box
      borderBottom="1px solid #ffffff33"
      color="#fff"
      background="linear-gradient(82.08deg, #12141C 36%, #DC2C0C 400%)"
      paddingInline={{ base: '20px', md: '100px' }}
      py={{ base: '96px' }}
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <NextLink
        href={refineUrlDomain(buttonData?.linkUrl, region, lang)}
        passHref
      >
        <Button
          target={isOpenInNewTab(buttonData?.linkUrl) ? '_blank' : ''}
          height="auto"
          as={Link}
          rel="noopener noreferrer"
          fontWeight={600}
          borderWidth={'1px'}
          width={{ base: '100%', md: '295px' }}
          borderRadius={'55px'}
          fontFamily="Geist"
          // textTransform="uppercase"
          color={'#FFFFFF'}
          fontSize="18px"
          display="flex"
          alignItems="center"
          p="1rem"
          bgColor={'transparent'}
          minW="250px"
          maxW={295}
          _hover={{ bgColor: '#FFFFFF1A', textDecor: 'none' }}
          letterSpacing="-0.54px"
          {...InnerSt[lang].buttonText} // style matching this button with inner button
        >
          {buttonData?.buttonText}
        </Button>
      </NextLink>
    </Box>
  );
};

export const MiracleInAction = ({ sectionContent, region, lang }) => {
  return (
    <>
      <Box
        borderBlock="1px solid #ffffff33"
        color="#fff"
        background="linear-gradient(82.08deg, #12141C 36%, #DC2C0C 400%)"
        paddingInline={{ base: '0px', md: '60px' }}
        pt={{ base: '80px', md: '120px' }}
      >
        <Box
          width="100%"
          position="relative"
          maxW={{ base: '1250px' }}
          mx="auto"
          display="flex"
          flexDirection="column"
        >
          <MainHeading
            region={region}
            lang={lang}
            sectionContent={sectionContent}
          />

          {/* slider main */}
          <SliderMain
            region={region}
            lang={lang}
            sectionContent={sectionContent}
          />
        </Box>
      </Box>
      {/* lower button */}
      <LowerButton
        region={region}
        lang={lang}
        sectionContent={sectionContent}
      />
    </>
  );
};
